import { BlcLoader, FlexBox } from '@Backlot-Cars/archie';
import FormContainer from 'components/FormContainer';
import PublicLayout from 'components/PublicSiteLayout/Base';
import SignUpOneColumnLayout from 'components/PublicSiteLayout/SignUpOneColumn';
import STATUS_CODES from 'constants/statusCodes';
import { SIGNUP_PATH, SIGNUP_PATH_IDS } from 'constants/signUp';
import urlsContext from 'contexts/urls/urlsContext';
import { segmentTrack } from 'helpers/integrations/SegmentSnippet';
import { getRecaptchaV3Token, getRecaptchaVersionParam, isRecaptchaVersion, recaptchaEnabled } from 'helpers/recaptcha';
import signup, { getAnonymousUser } from 'helpers/signUp';
import initialStateFunction from 'helpers/signUp/initialState';
import { validationsArray } from 'helpers/signUp/validation';
import getTreatments from 'helpers/split/getTreatments';
import { treatments as treats } from 'helpers/split/treatments';
import useIntercomBoot from 'hooks/intercom/useIntercomBoot';
import useTreatments from 'hooks/useTreatments';
import useWindowAnalytics from 'hooks/useWindowAnalytics';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Router, { useRouter } from 'next/router';
import { StepOne, StepThree, StepTwo, StepZero } from 'partials/sign_up';
import AfcAuthFailureBanner from 'partials/sign_up/AfcAuthFailureBanner';
import {
	AuctionAccessSignupContent,
	AuctionAccessSignupTopContent,
	AUCTION_ACCESS_SIGNUP_STAGE,
	isVerifiedAuctionAccessSignup,
	useAuctionAccessSignup,
} from 'partials/sign_up/AuctionAccessSignup';
import AuctionAccessLookupStart from 'partials/sign_up/AuctionAccessSignup/Intro';
import BUSINESS_TYPES, { getBusinessTypeOptionFromRails } from 'partials/sign_up/data/businessTypes';
import {
	isAfcOktaUser,
	isFastPath,
	isAffiliatePartner,
	hasPathId,
	invalidAfcOktaCredentials,
	isFastPathSignUp,
	isAfcAuthFailureRedirect,
} from 'partials/sign_up/helpers';
import { isEmailTakenError, isDuplicateEmailRedirectError } from '../../helpers/signUp/errorUtils';
import getSignUpPolicies from 'partials/sign_up/policies/getPolicies';
import { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import reducer from 'reducers/sign_up';
import SignUpServices from 'services/sign_up';
import { wrapper } from 'store';
import { setTreatments } from 'store/slices/treatments/treatmentsSlice';

const goToCongratsPage = ({ previewMode, signUpEmail, utmMedium, message, title, isAFC, isAuctionAccess = false }) => {
	const query = { previewMode, email: signUpEmail, message, title };
	if (utmMedium) query.utm_medium = utmMedium;
	if (isAFC !== undefined) query.isAFC = isAFC;
	if (isAuctionAccess) query.auctionAccess = true;
	Router.push({ pathname: '/sign_up/congrats', query });
};

const goToDisqualificationPage = () => {
	Router.push({ pathname: '/sign_up/interest' });
};

const reloadStepZeroWithRecaptchaVersion = (version, query) => {
	query.recaptcha = getRecaptchaVersionParam(version);
	Router.push({ pathname: '/sign_up', query });
};

const hasRecaptchaResponse = (isRecaptchaV2, step) => {
	if (recaptchaEnabled && isRecaptchaV2 && step === 0) {
		return window?.grecaptcha?.getResponse();
	}
	return true;
};

const getRecaptchaToken = async (isRecaptchaV2, step) => {
	return recaptchaEnabled && !isRecaptchaV2 && step === 0 ? await getRecaptchaV3Token('SIGN_UP_STEP_ZERO') : null;
};

const getSignupPathId = (query) =>
	isFastPath(query) ? SIGNUP_PATH_IDS.privateLabelPath
	: isAfcOktaUser(query) || invalidAfcOktaCredentials(query) ? SIGNUP_PATH_IDS.partnerFastPath
	: isAffiliatePartner(query) ? SIGNUP_PATH_IDS.standardPartnerPath
	: SIGNUP_PATH_IDS.standardPath;

const shouldCollectBusinessTypeOnResume = (values, lastStep, isOneSingleStep) =>
	!isOneSingleStep &&
	!(lastStep > 0 && isVerifiedAuctionAccessSignup(values[0]?.auctionAccess)) &&
	!getBusinessTypeOptionFromRails(values[0]?.businessType);

const SignUp = (props) => {
	const DATE_DEFAULT_FORMAT = 'MM-DD-YYYY';
	const DATE_VALID_FORMATS = ['MM-DD-YYYY', 'MM-D-YYYY', 'M-DD-YYYY', 'M-D-YYYY'];
	const { query, isReady, pathname, replace } = useRouter();
	const formValues = useSelector((state) => state.signUp.formValues);
	const initialState = initialStateFunction({
		...formValues,
		token: query.sign_up_token,
	});
	const [state, dispatch] = useReducer(reducer, initialState);
	const { signInUrl } = useContext(urlsContext);
	const { t } = useTranslation(['common', 'signup']);
	const [bootIntercom, setBootIntercom] = useState(false);
	const [signUpEmail, setSignUpEmail] = useState('');
	const [aaOptionShown, setAaOptionShown] = useState(null);
	const didTrackSignupView = useRef(false);
	const isOneSingleStep = isAfcOktaUser(query) || isFastPathSignUp(query) || isAffiliatePartner(query);
	const isRecaptchaV2 = isRecaptchaVersion(2, query);
	const driversLicenseInStepZero = query.driversLicense === 'true';
	const auctionAccessOnboardingEnabled = useTreatments(treats.auction_access_onboarding_enabled);

	if (!query.pathid || query.pathid.length == 0) query.pathid = getSignupPathId(query);

	useIntercomBoot(bootIntercom);

	const descriptionOl =
		"BacklotCars is now OPENLANE. We're making automotive wholesale easy. Request an invite today!";

	const onSubmitStep = async (values, step) => {
		dispatch({ type: 'onSetError', error: '' });
		if (hasRecaptchaResponse(isRecaptchaV2, step)) {
			if (!isOneSingleStep && step === 0 && !state.backButtonClicked) {
				try {
					const res = await SignUpServices.instance.getSignUpPartialDataByEmail(values.email);
					if (res.status === STATUS_CODES.OK) {
						// Allow partial users to resume sign up by inputting their email address at step 0.
						await resumeSignUp(res.data.data, step, values);
					}
				} catch (res) {
					if (Boolean(res.error) && res.status === STATUS_CODES.UNAUTHORIZED) {
						// If the email doesn't exist then this is a new sign up, proceed normally
						await submitStep(step, values);
					} else if (Boolean(res.error) && isEmailTakenError(res.error)) {
						dispatch({
							type: 'onGetPartialDataError',
							error: t('signup:errors.emailAlreadyExists'),
						});
					} else {
						dispatch({ type: 'onGetPartialDataError', error: t('common:error') });
					}
				}
			} else {
				await submitStep(step, values);
			}
		} else {
			dispatch({ type: 'onSetError', error: t('common:recaptchaInvalid') });
		}
	};

	const submitStep = async (step, values, resumeStep, { isAuctionAccess = false } = {}) => {
		const recaptchaToken = await getRecaptchaToken(isRecaptchaV2, step);
		const body = await signup.build(
			step,
			values,
			state.token,
			signUpEmail,
			query,
			props.onboardingLanguage,
			resumeStep,
			recaptchaToken,
			{
				anonymous_id: props.anonymousUser.user_id,
				...(isAuctionAccess ? {} : { aa_option_shown: aaOptionShown }),
				fall_2024_dealer_radio_removal: props.treatments.fall_2024_dealer_radio_removal,
				okta_signup_integration: true,
			},
		);

		try {
			const { data } = await SignUpServices.instance.confirmStepUpdate(body);

			if (isDuplicateEmailRedirectError(data)) {
				Router.push({
					pathname: '/sign_in',
					query: { duplicate_email: 'true', email: encodeURIComponent(values.email) },
				});

				return false;
			}

			if (isOneSingleStep) {
				const isAFC = isAfcOktaUser(query);
				goToCongratsPage({
					previewMode: data.preview_mode,
					signUpEmail: values.email,
					utmMedium: query.utm_medium,
					message: data.message,
					title: data.title,
					isAFC,
				});
			} else if (step === 3) {
				goToCongratsPage({
					previewMode: data.preview_mode,
					signUpEmail,
					utmMedium: query.utm_medium,
					message: data.message,
					title: data.title,
					isAFC: false,
					isAuctionAccess,
				});
			} else if (step === 2) {
				goToCongratsPage({
					previewMode: data.preview_mode,
					signUpEmail,
					utmMedium: query.utm_medium,
					message: data.message,
					title: data.title,
					isAFC: false,
				});
			} else {
				goToNextPage(data, step, values);
			}
			return true;
		} catch (res) {
			if (Boolean(res.error) && res.error.message) {
				if (isEmailTakenError(res.error)) {
					dispatch({
						type: 'onSetError',
						error: t('signup:errors.emailAlreadyExists'),
					});
					return false;
				} else {
					dispatch({ type: 'onSetError', error: res.error.message });
				}
			} else if (res.status === STATUS_CODES.FORBIDDEN) {
				goToDisqualificationPage();
			} else if (res.status === STATUS_CODES.CONFLICT) {
				// rails detected a non-human registration attempt
				reloadStepZeroWithRecaptchaVersion(2, query); // fall back to the old recaptcha challenge UI
				return false; // return false to prevent moving to the next step
			} else {
				dispatch({ type: 'onSetError', error: t('common:error') });
			}
			return false;
		}
	};

	const goToNextPage = (data, step, values) => {
		setSignUpEmail(data.signup_email);
		if (step >= 1) {
			setBootIntercom(true);
		}
		dispatch({ type: 'onSaveStepSuccess', token: data.token, step, values });
		updateStepURLQuery(state.step + 1);
	};

	const onBack = () => {
		updateStepURLQuery(state.step - 1);
		dispatch({ type: 'onBack' });
	};

	const resumeSignUp = async (data, step, values) => {
		// Save last_step and the new step 0 data in rails
		const resumeStep = data.last_step;
		const submitted = await submitStep(step, values, resumeStep);
		if (!submitted) return;

		// Save the new step 0 data in the store
		const oldValues = [...data.steps_data];
		oldValues[0].firstName = values.firstName;
		oldValues[0].lastName = values.lastName;
		oldValues[0].mobilePhone = values.mobilePhone;
		oldValues[0].dealershipName = values.dealershipName;
		oldValues[0].auctionAccessDealerId = values.auctionAccessDealerId;

		const businessTypeValue = parseInt(values.businessType);
		const businessType = BUSINESS_TYPES.find((type) => type.value == businessTypeValue);
		if (businessType) {
			oldValues[0].businessType = businessType.railsValue;
		}

		oldValues[0].marketplaceDocuments = [];
		for (const key in values) {
			if (typeof values[key] === 'object' && values[key]?.hasOwnProperty('id')) {
				oldValues[0].marketplaceDocuments.push({ id: values[key].id, type: key });
			}
		}

		// Instead of going to step 1, go to the user's original stopping point
		dispatch({
			type: 'onGetPartialData',
			values: oldValues,
			step: resumeStep,
			resumeStep: resumeStep + 1,
			loadedPartialData: true,
		});
		updateStepURLQuery(resumeStep + 1);
	};

	const renderStep = ({ currentStep: step, onSubmit, initialValues }) => {
		const components = {
			0: StepZero,
			1: StepOne,
			2: StepTwo,
			3: StepThree,
		};

		if (!components[step]) return null;

		const Component = components[step];
		const validationSchema = validationsArray[step]({
			...props,
			t,
			isFastPath: isFastPathSignUp(query),
			isAfcFastPath: isAfcOktaUser(query),
			isAffiliatePartner: isAffiliatePartner(query),
			isRecaptchaV2,
			driversLicenseInStepZero,
		});
		const componentProps = {
			...props,
			signInUrl,
			onBack,
			error: state.error,
			dateValidFormats: DATE_VALID_FORMATS,
			dateDefaultFormat: DATE_DEFAULT_FORMAT,
			validationSchema,
			onSubmit,
			initialValues,
			resumeStep: state.resumeStep,
			backButtonClicked: state.backButtonClicked,
			treatments: props.treatments,
		};

		return <Component {...componentProps} />;
	};

	const renderForm = (currentStep) => {
		if (!auctionAccessSignup.shouldRenderStandardSignup) {
			return (
				<AuctionAccessSignupContent
					anonymousId={props.anonymousUser.user_id}
					controller={auctionAccessSignup}
					signInUrl={signInUrl}
					signUpPolicies={props.signUpPolicies}
					state={state}
				/>
			);
		}

		const onSubmit = async (values) => {
			await onSubmitStep(values, currentStep);
		};

		const step = renderStep({ currentStep, onSubmit, initialValues: state.values[currentStep] });

		return (
			<FormContainer>
				{shouldShowAuctionAccessOption && (
					<AuctionAccessLookupStart onStart={auctionAccessSignup.startAuctionAccessLookup} />
				)}
				{step}
			</FormContainer>
		);
	};

	const updateStepURLQuery = (step, { removeSignUpToken = false } = {}) => {
		const updatedQuery = { ...query, step };
		if (removeSignUpToken || (state.token && query.sign_up_token && state.token !== query.sign_up_token)) {
			delete updatedQuery.sign_up_token;
		}
		replace(
			{
				pathname: hasPathId(SIGNUP_PATH_IDS.privateLabelPath, query) ? SIGNUP_PATH.privateLabelPath : pathname,
				query: updatedQuery,
			},
			undefined,
			{ shallow: true },
		);
	};

	const auctionAccessSignup = useAuctionAccessSignup({
		anonymousUser: props.anonymousUser,
		dispatch,
		enabled: auctionAccessOnboardingEnabled,
		isOneSingleStep,
		onboardingLanguage: props.onboardingLanguage,
		query,
		setSignUpEmail,
		signInUrl,
		signUpEmail,
		signUpPolicies: props.signUpPolicies,
		state,
		submitStep,
		treatments: props.treatments,
		updateStepURLQuery,
	});
	const shouldShowAuctionAccessOption =
		state.loadedPartialData &&
		state.step === 0 &&
		auctionAccessSignup.shouldRenderStandardSignup &&
		auctionAccessSignup.stage === AUCTION_ACCESS_SIGNUP_STAGE.START;

	const shouldRenderAuctionAccessTopContent =
		auctionAccessSignup.isOnboardingFlow && auctionAccessSignup.stage !== AUCTION_ACCESS_SIGNUP_STAGE.START;
	const signUpTopContent = (isAfcAuthFailureRedirect(query) || shouldRenderAuctionAccessTopContent) && (
		<>
			{isAfcAuthFailureRedirect(query) && <AfcAuthFailureBanner />}
			<AuctionAccessSignupTopContent
				isOnboardingFlow={shouldRenderAuctionAccessTopContent}
				stage={auctionAccessSignup.stage}
			/>
		</>
	);

	useEffect(() => {
		if (state.loadedPartialData && aaOptionShown === null) {
			setAaOptionShown(shouldShowAuctionAccessOption);
		}
	}, [aaOptionShown, shouldShowAuctionAccessOption, state.loadedPartialData]);

	const trackSignupView = useCallback(() => {
		if (aaOptionShown === null || didTrackSignupView.current) return;

		didTrackSignupView.current = true;
		segmentTrack('Signup viewed', {
			aa_option_shown: aaOptionShown,
			properties: {
				viewed_at: new Date().toISOString(),
			},
		});
	}, [aaOptionShown]);

	useWindowAnalytics(trackSignupView);

	useEffect(() => {
		async function loadPartialData(token) {
			const res = await SignUpServices.instance.getSignUpPartialData(token);
			if (res.status === STATUS_CODES.OK) {
				const step = res.data.data.last_step;
				const values = [...res.data.data.steps_data];
				const startStep = shouldCollectBusinessTypeOnResume(values, step, isOneSingleStep) ? 0 : step + 1;
				dispatch({
					token,
					type: 'onGetPartialData',
					values,
					step,
					startStep,
					resumeStep: step + 1,
					loadedPartialData: true,
				});
				updateStepURLQuery(startStep);
			} else if (res.status === STATUS_CODES.UNAUTHORIZED) {
				dispatch({ type: 'onGetPartialDataError', error: t('signup:inactivityError') });
			} else {
				dispatch({ type: 'onGetPartialDataError', error: t('common:error') });
			}
		}
		const signUpToken = state.token || query.sign_up_token;

		if (!state.loadedPartialData && signUpToken) {
			loadPartialData(signUpToken);
		} else if (isReady && !signUpToken) {
			dispatch({ type: 'onSetLoadedPartialData' });
		}
	}, [query.sign_up_token, isReady]); //eslint-disable-line

	useEffect(() => {
		if (query?.step !== state.step) {
			updateStepURLQuery(state.step);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<PublicLayout
			isPublicPage
			id="signup"
			meta={{
				title: t('common:signUp'),
				description: descriptionOl,
			}}
		>
			<SignUpOneColumnLayout
				step={state.step}
				hideProgressBar={auctionAccessSignup.isOnboardingFlow}
				TopContent={signUpTopContent}
				CardContent={
					<>
						{state.loadedPartialData ?
							renderForm(state.step)
						:	<FlexBox justifyContent="center" alignItems="center" width="100%" padding="md">
								<BlcLoader size="larger" />
							</FlexBox>
						}
					</>
				}
			/>
		</PublicLayout>
	);
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
	const spanishLanguageCode = 1;
	const englishLanguageCode = 0;
	const { locale } = ctx;
	const onboardingLanguage = ctx.locale.includes('es') ? spanishLanguageCode : englishLanguageCode;
	const signUpPolicies = isFastPathSignUp(ctx?.query) ? [] : await getSignUpPolicies();
	const anonymousUser = getAnonymousUser();
	const treatments = await getTreatments({
		treatments: [
			treats.fall_2024_dealer_radio_removal,
			treats.address_autocomplete_signup,
			treats.auction_access_onboarding_enabled,
		],
		user: anonymousUser,
	});
	await store.dispatch(setTreatments({ treatments }));

	return {
		props: {
			...(await serverSideTranslations(locale, ['common', 'signup'])),
			onboardingLanguage,
			signUpPolicies,
			treatments,
			anonymousUser,
		},
	};
});

export default SignUp;
